'use client';
import React from 'react';
import { Download, Printer } from 'lucide-react';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';
import { GravePlot } from './GravePlotContent';

interface Props {
  plot: GravePlot;
  onClose: () => void;
}

export default function PlotQRModal({ plot, onClose }: Props) {
  // QR code represented as a visual grid pattern (placeholder — replace with real QR library)
  const qrGrid = Array.from({ length: 21 }).map((_, row) =>
    Array.from({ length: 21 }).map((_, col) => {
      // Corner squares
      if ((row < 7 && col < 7) || (row < 7 && col > 13) || (row > 13 && col < 7)) return true;
      // Timing patterns
      if (row === 6 && col % 2 === 0) return true;
      if (col === 6 && row % 2 === 0) return true;
      // Random-ish data modules based on plotId hash
      const seed = (plot.plotId.charCodeAt(row % plot.plotId.length) + col * 7 + row * 13) % 3;
      return seed === 0;
    })
  );

  return (
    <Modal open onClose={onClose} title="Plot QR Code" subtitle={plot.plotId} size="sm">
      <div className="flex flex-col items-center gap-5">
        {/* QR Visual */}
        <div className="p-4 bg-white border border-border rounded-xl shadow-card">
          <div className="grid gap-0" style={{ gridTemplateColumns: `repeat(21, 1fr)`, width: 168 }}>
            {qrGrid.map((rowArr, ri) =>
              rowArr.map((cell, ci) => (
                <div
                  key={`qr-${ri}-${ci}`}
                  className={`w-2 h-2 ${cell ? 'bg-foreground' : 'bg-white'}`}
                />
              ))
            )}
          </div>
        </div>

        {/* Plot Info */}
        <div className="w-full space-y-2">
          {[
            ['Plot ID', plot.plotId],
            ['Cemetery', plot.cemetery],
            ['Block / Row', `${plot.block} / ${plot.row}`],
            ['Status', plot.status],
            ['Deceased', plot.deceasedName],
            ['Burial Date', plot.burialDate],
            ['GPS', `${plot.gpsLat}, ${plot.gpsLng}`],
          ].map(([label, value]) => (
            <div key={`qr-info-${label}`} className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-xs text-muted-foreground">{label}</span>
              <span className={`text-xs font-600 ${label === 'Plot ID' || label === 'GPS' ? 'font-mono-data' : ''}`}>
                {value}
              </span>
            </div>
          ))}
        </div>

        <div className="flex gap-3 w-full">
          <button
            onClick={() => { toast.success('QR code downloaded'); }}
            className="btn-primary flex-1 justify-center text-sm"
          >
            <Download size={14} /> Download QR
          </button>
          <button
            onClick={() => { toast.success('Sent to printer'); }}
            className="btn-secondary flex-1 justify-center text-sm"
          >
            <Printer size={14} /> Print
          </button>
        </div>
      </div>
    </Modal>
  );
}